import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Checkbox, Input, Switcher, TextField } from '@shared/ui/kit';
import stylex from '@stylexjs/stylex';
import { CalendarIcon, LockIcon, XmarkIcon, ChevronDownIcon } from '@shared/assets/icons';
import { colors } from '@shared/tokens/colors.stylex';
import { useToast } from '@shared/hooks/useToast';
import useGetSettingsByEventId from '@features/api/use-get-settings';
import { formatDateTime } from '@shared/libs/format-date';
import { calcSmsParts20_70 } from '@shared/libs/sms-utils';
import type { SettingsPopupProps } from './model/types';
import { styles } from './styles';
import {
  TicketDeliveryMethod,
  useUpdateSettings,
  type UpdateSettingInput,
} from '@features/modal-invitation/api/updateSetting';
import { getTextFromHtml } from '@features/modal-invitation/lib/getTextFromHtml';
import { formatTicketDeliveryMethods } from './lib/format-ticket-delivery-methods';
import {
  buildEventDaySendingAt,
  EVENT_DAY_TIME_ERROR,
  parseEventDaySendingTime,
  sanitizeTimePart,
  splitEventDaySendingAt,
} from './lib/event-day-sending-time';
import { Drawer } from '@shared/ui/drawer';

export const SettingsPopup: FC<SettingsPopupProps> = ({ isVisible, onClose, totalInvites = 0, eventDateStart }) => {
  const { id } = useParams<{ id: string }>();

  const { data, error } = useGetSettingsByEventId(Number(id));
  const { updateSettings } = useUpdateSettings();
  const [isSwitcherActive, setIsSwitcherActive] = useState<boolean>(false);
  const [isEventDaySendingActive, setIsEventDaySendingActive] = useState<boolean>(false);
  const [eventDayHour, setEventDayHour] = useState<string>('');
  const [eventDayMinute, setEventDayMinute] = useState<string>('');
  const [hasEventDayTimeError, setHasEventDayTimeError] = useState<boolean>(false);

  const { showToast } = useToast();

  useEffect(() => {
    if (error) {
      showToast('Ошибка при получении данных', 'error');
    }
  }, [error]);

  useEffect(() => {
    if (isVisible && data) {
      setIsSwitcherActive(data.showFioInApp);
      setIsEventDaySendingActive(Boolean(data.sendTicketsOnEventDayAt));

      const { hour, minute } = splitEventDaySendingAt(data.sendTicketsOnEventDayAt);
      setEventDayHour(hour);
      setEventDayMinute(minute);
      setHasEventDayTimeError(false);
    }
  }, [isVisible, data]);

  const handleSwitcherChange = () => {
    setIsSwitcherActive((prevState) => !prevState);
  };

  const handleEventDaySendingChange = () => {
    setIsEventDaySendingActive((prevState) => !prevState);
    setHasEventDayTimeError(false);
  };

  const handleEventDayHourChange = (value: string) => {
    setEventDayHour(sanitizeTimePart(value));
    setHasEventDayTimeError(false);
  };

  const handleEventDayMinuteChange = (value: string) => {
    setEventDayMinute(sanitizeTimePart(value));
    setHasEventDayTimeError(false);
  };

  const handleSave = async () => {
    const formData: UpdateSettingInput = {
      showFioInApp: isSwitcherActive,
      // null очищает момент отправки на бэкенде и выключает её
      sendTicketsOnEventDayAt: null,
    };

    if (isEventDaySendingActive) {
      const sendingTime = parseEventDaySendingTime(eventDayHour, eventDayMinute);

      if (!sendingTime) {
        setHasEventDayTimeError(true);
        showToast(EVENT_DAY_TIME_ERROR, 'error');
        return;
      }

      if (!eventDateStart) {
        showToast('Не удалось определить дату начала мероприятия', 'error');
        return;
      }

      formData.sendTicketsOnEventDayAt = buildEventDaySendingAt(eventDateStart, sendingTime);
    }

    try {
      const response = await updateSettings({ eventId: Number(id), formData });

      if (response.error) {
        showToast('Ошибка выполнения мутации', 'error');
        return;
      }

      showToast('Настройки успешно сохранены', 'success');
      onClose();
    } catch (error) {
      showToast('Ошибка при сохранении настроек', 'error');
    }
  };

  if (error) return <p>Ошибка: {error.message}</p>;
  if (!data) return null;

  const hasNoData = totalInvites === 0;
  const ticketPlainText = getTextFromHtml(
    data.ticketDeliveryMessage?.trim() || data.emailTicketDeliveryMessage || ''
  ).trim();
  const isSmsTicketMode = data.ticketDeliveryMethod?.includes(TicketDeliveryMethod.SMS) ?? false;
  const ticketDeliveryMethodLabel = formatTicketDeliveryMethods(data.ticketDeliveryMethod);

  return (
    <Drawer
      isOpen={isVisible}
      onClose={onClose}
      position="right"
      width="580px"
      headerContent={
        <div {...stylex.props(styles.headerPopupContainer)}>
          <span>Настройка мероприятия</span>
          <button {...stylex.props(styles.closeIcon)} onClick={onClose}>
            <XmarkIcon size="24px" color={colors.primaryIcon} />
          </button>
        </div>
      }
      showCloseButton={false}
    >
      <div {...stylex.props(styles.contentWrapper)}>
        <div {...stylex.props(styles.container)}>
          <div {...stylex.props(styles.content)}>
            {!hasNoData && (
              <>
                <section {...stylex.props(styles.sectionInvite)}>
                  <div {...stylex.props(styles.sectionInviteHeader)}>
                    <span {...stylex.props(styles.sectionInviteTitle)}>Приглашениe</span>
                    <span {...stylex.props(styles.sectionInviteText)}>
                      Вы можете изменить срок действия приглашения указав конечные дату и время
                    </span>
                  </div>
                  <div {...stylex.props(styles.invitationParams)}>
                    <div {...stylex.props(styles.fieldWrapper)}>
                      <div {...stylex.props(styles.labelContainer)}>
                        <p {...stylex.props(styles.labelTypeBlock)}>Дата отправки приглашения</p>
                        <div {...stylex.props(styles.labelIcon)}>
                          <LockIcon />
                        </div>
                      </div>
                      <div {...stylex.props(styles.lockedDateContainerBlock)}>
                        <div {...stylex.props(styles.lockedDateTextBlock)}>
                          {data.inviteSentAt ? formatDateTime(data.inviteSentAt) : '-'}

                          <div {...stylex.props(styles.calendarIcon)}>
                            <CalendarIcon />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div {...stylex.props(styles.fieldWrapper)}>
                      <div {...stylex.props(styles.labelContainer)}>
                        <p {...stylex.props(styles.labelType)}>Срок действия приглашения</p>
                        <div {...stylex.props(styles.labelIcon)}>{/* <LockIcon /> */}</div>
                      </div>
                      <div {...stylex.props(styles.lockedDateContainer)}>
                        <div {...stylex.props(styles.lockedDateText)}>
                          {data.invitationExpiresAt ? formatDateTime(data.invitationExpiresAt) : '-'}
                          <div {...stylex.props(styles.calendarIcon)}>
                            <CalendarIcon />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section {...stylex.props(styles.sectionInvite)}>
                  <div {...stylex.props(styles.sectionInviteHeader)}>
                    <span {...stylex.props(styles.sectionInviteTitle)}>Билет</span>
                    <span {...stylex.props(styles.sectionInviteText)}>
                      Вы можете изменить характеристики отправки билета
                    </span>
                  </div>
                  <div {...stylex.props(styles.invitationParams)}>
                    <div {...stylex.props(styles.fieldWrapper)}>
                      <div {...stylex.props(styles.labelContainer)}>
                        <p {...stylex.props(styles.labelType)}>Дата отправки билета</p>
                        <div {...stylex.props(styles.labelIcon)}>{/* <LockIcon /> */}</div>
                      </div>
                      <div {...stylex.props(styles.lockedDateContainer)}>
                        <div {...stylex.props(styles.lockedDateText)}>
                          {data.sendTicketAfterAcceptAt ? formatDateTime(data.sendTicketAfterAcceptAt) : '-'}
                          <div {...stylex.props(styles.calendarIcon)}>
                            <CalendarIcon />
                          </div>
                        </div>
                      </div>

                      <div {...stylex.props(styles.skipTicketsRow)}>
                        <Checkbox isChecked={data.skipTicketsOnAccept ?? true} onChange={() => {}} />
                        <p {...stylex.props(styles.skipTicketsText)}>Не отправлять билеты</p>
                      </div>
                    </div>

                    <div {...stylex.props(styles.fieldWrapper)}>
                      <div {...stylex.props(styles.labelContainer)}>
                        <p {...stylex.props(styles.labelType)}>Тип отправки билета</p>
                        <div {...stylex.props(styles.labelIcon)}>{/* <LockIcon /> */}</div>
                      </div>
                      <div {...stylex.props(styles.lockedDropdownContainer)}>
                        <div {...stylex.props(styles.lockedDropdownText)}>
                          {ticketDeliveryMethodLabel}
                          <div {...stylex.props(styles.dropdownIcon)}>
                            <ChevronDownIcon />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
                <section>
                  <p {...stylex.props(styles.textCount)}>
                    <span>Текст билета</span>
                    {isSmsTicketMode && `${ticketPlainText.length} (${calcSmsParts20_70(ticketPlainText)} смс)`}
                  </p>
                  <TextField
                    labelMsg=""
                    placeholder="Ввести текст"
                    value={ticketPlainText}
                    changeFunction={() => {}}
                    settings={{
                      width: '100%',
                      cursor: 'default',
                      pointerEvents: 'none',
                      resize: 'none',
                      height: '78px',
                      minHeight: '64px',
                      overflow: 'auto',
                    }}
                  />
                </section>
              </>
            )}
            <section {...stylex.props(styles.sectionInviteArea)}>
              <div {...stylex.props(styles.sectionInviteHeader)}>
                <span {...stylex.props(styles.sectionInviteTitle)}>Билет в день мероприятия</span>
                <span {...stylex.props(styles.sectionInviteText)}>
                  Участникам, принявшим приглашение, билеты будут отправлены в день начала события в указанное время.
                  Отправка идёт только в VK Teams
                </span>
              </div>
              <div {...stylex.props(styles.switcherText)}>
                <Switcher
                  size="small"
                  isActive={isEventDaySendingActive}
                  changeFunction={handleEventDaySendingChange}
                />
                <p>Отправить билеты в день начала события</p>
              </div>
              {isEventDaySendingActive && (
                <>
                  <div {...stylex.props(styles.eventDayTimeRow)}>
                    <Input
                      size="small"
                      labelText="Часы"
                      placeholder="00"
                      value={eventDayHour}
                      changeFunction={handleEventDayHourChange}
                      status={hasEventDayTimeError ? 'error' : 'default'}
                      settings={{ width: '80px' }}
                    />
                    <span {...stylex.props(styles.eventDayTimeSeparator)}>:</span>
                    <Input
                      size="small"
                      labelText="Минуты"
                      placeholder="00"
                      value={eventDayMinute}
                      changeFunction={handleEventDayMinuteChange}
                      status={hasEventDayTimeError ? 'error' : 'default'}
                      settings={{ width: '80px' }}
                    />
                  </div>
                  {hasEventDayTimeError && <p {...stylex.props(styles.eventDayTimeError)}>{EVENT_DAY_TIME_ERROR}</p>}
                </>
              )}
            </section>
            <section {...stylex.props(styles.sectionInviteArea)}>
              <div {...stylex.props(styles.sectionInviteHeader)}>
                <span {...stylex.props(styles.sectionInviteTitle)}>Мобильное приложение</span>
                <span {...stylex.props(styles.sectionInviteText)}>
                  Отображение при регистрации ФИО сотрудника, принявшего приглашение
                </span>
              </div>
              <div {...stylex.props(styles.switcherText)}>
                <Switcher size="small" isActive={isSwitcherActive} changeFunction={handleSwitcherChange} />
                <p>Показать</p>
              </div>
            </section>
          </div>
        </div>
        <div {...stylex.props(styles.navigation)}>
          <Button
            size="small"
            settings={{ padding: '8px 20px', fontSize: '14px' }}
            variant="secondary"
            onClick={onClose}
          >
            <p>Отменить</p>
          </Button>
          <Button
            size="small"
            variant="accent"
            settings={{ padding: '8px 20px', fontSize: '14px' }}
            onClick={handleSave}
          >
            <p>Сохранить</p>
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
